import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { apiError } from "@/context/AuthContext";
import { WarningBadge } from "@/components/Badges";
import { toast } from "sonner";
import { ScanSearch, Search as SearchIcon, Camera, Loader2, Type, AlertTriangle } from "lucide-react";
import CameraCapture from "@/components/CameraCapture";
import { useRef } from "react";

function ResultCard({ r }) {
  return (
    <Link
      to={`/danh-sach-den/${r.id}`}
      data-testid={`search-result-${r.id}`}
      className="block bg-card border border-zinc-800 rounded-sm p-4 hover:border-primary/40 transition-colors"
    >
      <div className="flex items-start justify-between mb-2">
        <div className="font-semibold text-white">{r.customer_name}</div>
        <WarningBadge level={r.warning_level} />
      </div>
      {r.report_count > 1 && (
        <div data-testid={`repeat-${r.id}`} className="flex items-center gap-2 bg-red-500/15 border border-red-500 rounded-sm px-3 py-1.5 mb-2">
          <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
          <span className="text-xs font-bold text-red-300">Đã bị báo cáo {r.report_count} lần — cảnh giác cao!</span>
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
        <div className="flex justify-between"><span className="text-zinc-500">SĐT</span><span className="text-zinc-300">{r.phone || "—"}</span></div>
        <div className="flex justify-between"><span className="text-zinc-500">CCCD</span><span className="text-zinc-300 font-mono">{r.cccd_number || "—"}</span></div>
        <div className="flex justify-between"><span className="text-zinc-500">Bằng lái</span><span className="text-zinc-300">{r.license_number || "—"}</span></div>
        <div className="flex justify-between"><span className="text-zinc-500">Nhà xe</span><span className="text-zinc-300">{r.reported_by_name}</span></div>
      </div>
      <p className="text-zinc-400 text-sm mt-2 border-t border-zinc-800 pt-2">{r.reason}</p>
    </Link>
  );
}

export default function Search() {
  const [tab, setTab] = useState("text");
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [camOpen, setCamOpen] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const inputRef = useRef(null);

  const doText = async (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    setLoading(true);
    setExtracted(null);
    try {
      const { data } = await api.get("/search", { params: { q } });
      setResults(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const doImage = async (file) => {
    if (!file) return;
    setLoading(true);
    setResults(null);
    setExtracted(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/search/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setExtracted(data.extracted);
      setResults(data.matches);
      if (!data.matches?.length) toast.info("Không tìm thấy khách trùng khớp trong danh sách đen.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const tabCls = (t) =>
    `flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold rounded-sm transition-colors ${
      tab === t ? "bg-primary text-white" : "bg-transparent text-zinc-400 hover:text-white border border-zinc-800"
    }`;

  return (
    <div className="max-w-3xl">
      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-2">Công cụ</div>
      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-white flex items-center gap-3">
        <ScanSearch className="w-9 h-9 text-primary" /> Tra cứu nhanh
      </h1>
      <p className="text-zinc-500 mt-2 mb-6">Tra cứu bằng hình ảnh CCCD (AI tự nhận diện) hoặc nhập tay thông tin.</p>

      <div className="flex gap-2 mb-6">
        <button data-testid="tab-text" onClick={() => setTab("text")} className={tabCls("text")}>
          <Type className="w-4 h-4" /> Nhập tay
        </button>
        <button data-testid="tab-image" onClick={() => setTab("image")} className={tabCls("image")}>
          <Camera className="w-4 h-4" /> Bằng hình ảnh
        </button>
      </div>

      {tab === "text" ? (
        <form onSubmit={doText} className="flex gap-2 mb-6">
          <div className="relative flex-1">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
            <input
              data-testid="search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Nhập tên, SĐT, số CCCD, bằng lái, địa chỉ..."
              className="w-full bg-[#0a0a0a] border border-zinc-800 text-white rounded-sm pl-10 pr-4 py-3 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors placeholder:text-zinc-600"
            />
          </div>
          <button data-testid="search-submit" disabled={loading} className="bg-primary hover:bg-[#ff1a1a] text-white font-bold px-6 rounded-sm transition-colors disabled:opacity-50 flex items-center gap-2">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <SearchIcon className="w-4 h-4" />} Tra
          </button>
        </form>
      ) : (
        <div className="mb-6 bg-card border border-zinc-800 rounded-sm p-6 text-center">
          <p className="text-zinc-400 text-sm mb-4">Chụp hoặc tải ảnh CCCD/bằng lái. Hệ thống AI sẽ đọc thông tin và dò tìm trong danh sách đen.</p>
          <div className="flex gap-3 justify-center">
            <button data-testid="img-upload-btn" onClick={() => inputRef.current?.click()} disabled={loading} className="flex items-center gap-2 border border-zinc-700 text-zinc-300 hover:text-white hover:border-primary rounded-sm px-5 py-2.5 text-sm transition-colors disabled:opacity-50">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <SearchIcon className="w-4 h-4" />} Tải ảnh
            </button>
            <button data-testid="img-camera-btn" onClick={() => setCamOpen(true)} disabled={loading} className="flex items-center gap-2 bg-primary hover:bg-[#ff1a1a] text-white font-bold rounded-sm px-5 py-2.5 text-sm transition-colors disabled:opacity-50">
              <Camera className="w-4 h-4" /> Chụp CCCD
            </button>
          </div>
          <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => doImage(e.target.files?.[0])} />
          {extracted && (
            <div className="mt-5 text-left bg-[#0a0a0a] border border-zinc-800 rounded-sm p-4">
              <div className="text-xs uppercase tracking-wider text-primary mb-2">Thông tin AI đọc được</div>
              <div className="text-sm text-zinc-300 space-y-1">
                <div>Họ tên: <span className="text-white font-medium">{extracted.full_name || "—"}</span></div>
                <div>Số CCCD: <span className="text-white font-mono">{extracted.cccd_number || "—"}</span></div>
                {extracted.dob && <div>Ngày sinh: <span className="text-white">{extracted.dob}</span></div>}
              </div>
            </div>
          )}
        </div>
      )}

      {results !== null && (
        <div>
          <div className="text-sm text-zinc-500 mb-3">
            {results.length > 0 ? `Tìm thấy ${results.length} kết quả` : "Không tìm thấy kết quả nào"}
          </div>
          <div className="space-y-3">
            {results.map((r) => <ResultCard key={r.id} r={r} />)}
          </div>
        </div>
      )}

      <CameraCapture open={camOpen} onClose={() => setCamOpen(false)} onCapture={doImage} title="Chụp CCCD để tra cứu" />
    </div>
  );
}
